import { useEffect, useState } from 'react';
import {
  Link as RouterLink,
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/LockOpenOutlined';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../contexts/AuthContext';
import { api, apiErrorMessage } from '../api/client';

type InviteState =
  | { status: 'none' }
  | { status: 'checking'; token: string }
  | { status: 'invalid'; token: string; message: string }
  | {
      status: 'valid';
      token: string;
      email: string;
      first_name: string | null;
      last_name: string | null;
      org_name: string | null;
    };

export default function SignupOrgPage() {
  const { user, loading, signupOrg } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite')?.trim() || '';

  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('school');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [invite, setInvite] = useState<InviteState>(
    inviteToken ? { status: 'checking', token: inviteToken } : { status: 'none' },
  );

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    setInvite({ status: 'checking', token: inviteToken });
    api
      .get(`/auth/signup-invite/${encodeURIComponent(inviteToken)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setInvite({
          status: 'valid',
          token: inviteToken,
          email: data.email,
          first_name: data.first_name ?? null,
          last_name: data.last_name ?? null,
          org_name: data.org_name ?? null,
        });
        setEmail((current) => current || data.email || '');
        setFirstName((current) => current || data.first_name || '');
        setLastName((current) => current || data.last_name || '');
        setOrgName((current) => current || data.org_name || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setInvite({
          status: 'invalid',
          token: inviteToken,
          message: apiErrorMessage(
            err,
            "This founding-coach invite is invalid or has already been used.",
          ),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signupOrg({
        org_name: orgName,
        org_type: orgType,
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        invite: invite.status === 'valid' ? invite.token : undefined,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your organization.'));
    } finally {
      setBusy(false);
    }
  };

  const isFoundingInvite = invite.status === 'valid';
  const subtitle = isFoundingInvite
    ? "You'll become the admin of your team. As a founding coach, you keep Baton free for life."
    : "You'll become the admin and can invite coaches next.";

  return (
    <AuthShell
      title="Create your organization"
      subtitle={subtitle}
      footer={
        <>
          Already have an account?{' '}
          <RouterLink to="/login" aria-label="Sign in to existing account">
            Sign in
          </RouterLink>
        </>
      }
    >
      {invite.status === 'checking' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Checking your founding-coach invite…
        </Alert>
      )}

      {invite.status === 'valid' && (
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: '#bcd5ee',
            bgcolor: '#eef4fb',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              icon={<LockIcon sx={{ fontSize: 16 }} />}
              label="Founding coach access"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 700,
                '& .MuiChip-icon': { color: 'primary.contrastText' },
              }}
            />
            <Typography variant="body2" sx={{ color: '#0b5394', fontWeight: 600 }}>
              {invite.email}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            This invite is one-time use. Free for life when you set up your team.
          </Typography>
        </Box>
      )}

      {invite.status === 'invalid' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {invite.message}{' '}
          <RouterLink to="/#waitlist" aria-label="Join the waitlist">
            Join the waitlist
          </RouterLink>{' '}
          to get a fresh link in your inbox.
        </Alert>
      )}

      {invite.status === 'none' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Founding-coach signup is by emailed link.{' '}
          <RouterLink to="/#waitlist" aria-label="Join the waitlist">
            Join the waitlist
          </RouterLink>{' '}
          — we'll email you a one-click link to set up your team. If you already got
          that email, open the link from it so the access token comes with you.
        </Alert>
      )}

      <form onSubmit={onSubmit} aria-label="Create organization form">
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Organization name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            required
            inputProps={{ 'aria-label': 'Organization name' }}
          />
          <TextField
            label="Type"
            select
            value={orgType}
            onChange={(e) => setOrgType(e.target.value)}
            inputProps={{ 'aria-label': 'Organization type' }}
          >
            <MenuItem value="school">School</MenuItem>
            <MenuItem value="club">Club / community</MenuItem>
            <MenuItem value="district">District</MenuItem>
          </TextField>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                inputProps={{ 'aria-label': 'First name' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                inputProps={{ 'aria-label': 'Last name' }}
              />
            </Grid>
          </Grid>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            inputProps={{ 'aria-label': 'Email address' }}
          />
          <TextField
            label="Password (min 8 chars)"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            inputProps={{ 'aria-label': 'Password' }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy || invite.status === 'checking'}
            aria-label="Create org"
          >
            {busy ? 'Creating…' : 'Create organization'}
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
}
